/**
 * Shared constants, formatters, and cross-page helpers.
 *
 * Extracted from App.js during the 2026-03 cleanup so page components under `/src/pages/`
 * can import them without pulling the entire App tree.
 */
import { useEffect, useState } from "react";
import axios from "axios";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const TOKEN_KEY = "paushtikk.token";
export const USER_KEY = "paushtikk.user";
export const IDENTITY_KEY = "paushtikk.identity";
export const DEFAULT_IDENTITY = { name: "Admin", isAdmin: true };

// ---------------------------------------------------------------------------
// LocalStorage readers
// ---------------------------------------------------------------------------
export const readIdentity = () => {
  try {
    const v = JSON.parse(localStorage.getItem(IDENTITY_KEY));
    return v && v.name ? v : DEFAULT_IDENTITY;
  } catch {
    return DEFAULT_IDENTITY;
  }
};
export const readAuthUser = () => {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY)) || null;
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------
// Formatters + tone helpers
// ---------------------------------------------------------------------------
export function scoreTone(score) {
  if (score >= 80) return "green";
  if (score >= 50) return "amber";
  return "slate";
}
export function conversionTone(rate) {
  if (rate >= 30) return "green";
  if (rate >= 10) return "amber";
  return "slate";
}
export function kbCategoryTone(category) {
  if (category === "plan") return "green";
  if (category === "policy") return "blue";
  if (category === "faq") return "amber";
  return "purple";
}
export function roleTone(role) {
  if (role === "ADMIN") return "purple";
  if (role.startsWith("SALES")) return "green";
  return "blue";
}
export function relTime(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}
export function initials(name) {
  return (name || "").split(" ").map(x => x[0]).join("").slice(0, 2);
}
/** Format any numeric amount as INR with Indian digit grouping (lakhs/crores). */
export function formatINR(n) {
  const value = Number(n) || 0;
  return `₹${value.toLocaleString("en-IN", { maximumFractionDigits: value >= 1000 ? 0 : 2 })}`;
}
/** Compact INR: ₹85,000 → ₹85K · ₹1,20,000 → ₹1.2L · ₹2,50,00,000 → ₹2.5Cr. */
export function formatINRCompact(n) {
  const v = Number(n) || 0;
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(v % 10000000 === 0 ? 0 : 1)}Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(v % 100000 === 0 ? 0 : 1)}L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}K`;
  return formatINR(v);
}
export function digitsOnly(phone) { return (phone || "").replace(/\D/g, ""); }
export function nudgeMessage(item) {
  const plan = item.plan_interest && item.plan_interest !== "Not captured"
    ? item.plan_interest.toLowerCase()
    : "meal plan";
  const first = (item.customer_name || "there").split(" ")[0];
  return `Hi ${first}, this is Paushtikk Switch. Just checking in on your ${plan} enquiry — happy to help you get started. When would be a good time to chat?`;
}
export function nudgeUrl(item) {
  return `https://wa.me/${digitsOnly(item.phone_number)}?text=${encodeURIComponent(nudgeMessage(item))}`;
}

// ---------------------------------------------------------------------------
// Session-persisted filter state (used by Leads / Active / Lost list pages)
// ---------------------------------------------------------------------------
export const DEFAULT_LIST_FILTERS = {
  date_range: "",
  date_from: "",
  date_to: "",
  category: "all",
  classification_source: "all",
  agent: "all",
  sales_stage: "all",
  follow_up: "all",
  lost_reason: "all",
};

export const DATE_RANGE_PRESETS = [
  { id: "", label: "All time" },
  { id: "today", label: "Today" },
  { id: "this_week", label: "This week" },
  { id: "this_month", label: "This month" },
  { id: "custom", label: "Custom" },
];
export const CATEGORY_OPTIONS = ["all", "new_lead", "marketing_lead", "active_customer"];
export const SOURCE_OPTIONS = ["all", "keyword", "llm", "interakt_tag"];
export const STAGE_OPTIONS = ["all", "NEW LEAD", "AI QUALIFYING", "QUALIFIED", "PAYMENT PENDING", "CONVERTED", "HUMAN HANDOVER", "LOST"];
export const FOLLOW_UP_OPTIONS = [
  { id: "all", label: "All follow-ups" },
  { id: "overdue", label: "Overdue" },
  { id: "today", label: "Due today" },
  { id: "upcoming", label: "Upcoming" },
];

export function useSessionFilters(key) {
  const [filters, setFilters] = useState(() => {
    try {
      const raw = sessionStorage.getItem(key);
      return raw ? { ...DEFAULT_LIST_FILTERS, ...JSON.parse(raw) } : DEFAULT_LIST_FILTERS;
    } catch { return DEFAULT_LIST_FILTERS; }
  });
  useEffect(() => {
    try { sessionStorage.setItem(key, JSON.stringify(filters)); } catch { /* ignore quota */ }
  }, [key, filters]);
  return [filters, setFilters];
}

export function toApiParams(filters) {
  const params = {};
  if (filters.date_range) {
    params.date_range = filters.date_range;
    if (filters.date_range === "custom") {
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
    }
  }
  if (filters.category && filters.category !== "all") params.category = filters.category;
  if (filters.classification_source && filters.classification_source !== "all") params.classification_source = filters.classification_source;
  if (filters.agent && filters.agent !== "all") params.agent = filters.agent;
  if (filters.sales_stage && filters.sales_stage !== "all") params.sales_stage = filters.sales_stage;
  if (filters.follow_up && filters.follow_up !== "all") params.follow_up = filters.follow_up;
  if (filters.lost_reason && filters.lost_reason !== "all") params.lost_reason = filters.lost_reason;
  return params;
}

// ---------------------------------------------------------------------------
// Small pre-configured axios helper (uses global auth interceptor set in App.js)
// ---------------------------------------------------------------------------
export { axios };
